import React, { useEffect, useRef, useState } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import * as WebIFC from "web-ifc";
import { useWatch } from "react-hook-form";
import { formConfig } from "../form/formConfig";
import { materialConfig } from "./materials/materialConfig";
import { TextureManager } from "../utils/TextureManager";
import { loadMaterialTextures as loadKTX2MaterialTextures } from "../utils/KTX2TextureLoader";
import { getCachedMaterial, setCachedMaterial, isMaterialCached } from "../utils/materialCache";

export const IFCModel = ({ ifcData, onMeshMapReady }) => {
  const { scene, camera, controls, gl } = useThree();
  const currentModelRef = useRef(null);
  const ifcApiRef = useRef(null);
  const modelIdRef = useRef(null);
  const materialMapRef = useRef(new Map()); // IFC material name -> [expressIds]
  const meshMapRef = useRef(new Map()); // IFC material name -> THREE.Mesh
  const formValues = useWatch();
  const textureManagerRef = useRef(new TextureManager());
  const hasInitializedCamera = useRef(false);
  const isLoadingNewModelRef = useRef(false);
  const [isIfcReady, setIsIfcReady] = useState(false);

  const USE_TEXTURE_MANAGER = true;

  // Initialize TextureManager with WebGL renderer
  useEffect(() => {
    if (gl && textureManagerRef.current) {
      textureManagerRef.current.setRenderer(gl);
      console.log('✅ TextureManager initialized with KTX2 support');
    }
  }, [gl]);

  // Initialize web-ifc API
  useEffect(() => {
    const initIfcApi = async () => {
      console.log('🔧 Initializing web-ifc API...');
      const ifcApi = new WebIFC.IfcAPI();

      // Set WASM path - use absolute URL to ensure correct loading
      // The second parameter (true) indicates it's an absolute path
      try {
        // Use CDN for reliable WASM loading (matches installed version 0.0.74)
        const wasmPath = 'https://unpkg.com/web-ifc@0.0.74/';
        console.log('🔧 Setting WASM path to:', wasmPath);
        ifcApi.SetWasmPath(wasmPath, true);
        await ifcApi.Init();
        console.log('✅ web-ifc initialized with CDN WASM');
      } catch (e) {
        console.error('❌ Failed to initialize web-ifc:', e);
        // Try local as fallback
        try {
          const localPath = window.location.origin + '/wasm/';
          console.log('🔧 Trying local WASM path:', localPath);
          ifcApi.SetWasmPath(localPath, true);
          await ifcApi.Init();
          console.log('✅ web-ifc initialized with local WASM');
        } catch (e2) {
          console.error('❌ Local WASM also failed:', e2);
          throw e2;
        }
      }

      ifcApiRef.current = ifcApi;
      setIsIfcReady(true);
    };

    initIfcApi();

    return () => {
      if (ifcApiRef.current) {
        ifcApiRef.current.Dispose();
      }
    };
  }, []);

  console.log('🔨 IFCModel component rendered');

  // Mesh selector matching (same as BackendModel)
  const matchesMeshSelector = (meshName, selector) => {
    if (!selector) return false;
    const values = Array.isArray(selector.value) ? selector.value : [selector.value];
    return values.some(value => {
      switch (selector.type) {
        case 'prefix': return meshName.startsWith(value);
        case 'exact': return meshName === value;
        case 'contains': return meshName.includes(value);
        case 'regex': return new RegExp(value).test(meshName);
        default: return false;
      }
    });
  };

  const isExcluded = (meshName, excluder) => {
    if (!excluder) return false;
    const values = Array.isArray(excluder.value) ? excluder.value : [excluder.value];
    return values.some(value => {
      switch (excluder.type) {
        case 'prefix': return meshName.startsWith(value);
        case 'exact': return meshName === value;
        case 'contains': return meshName.includes(value);
        case 'regex': return new RegExp(value).test(meshName);
        default: return false;
      }
    });
  };

  // Match on IFC color - the groupKey format is "materialName|r_g_b_a"
  // ifcColorSelector can match on the color portion of the groupKey
  // Example: { type: 'exact', value: '0.486_0.463_0.408_1.000' } matches walls
  // Example: { type: 'rgb', value: '0.486_0.463_0.408' } matches ignoring alpha
  const matchesColorSelector = (groupKey, selector) => {
    if (!selector) return false;

    // Extract color from groupKey (format: "materialName|r_g_b_a")
    const colorPart = groupKey.includes('|') ? groupKey.split('|')[1] : null;
    if (!colorPart) return false;

    const values = Array.isArray(selector.value) ? selector.value : [selector.value];
    return values.some(value => {
      switch (selector.type) {
        case 'exact':
          // Exact match on full color key (r_g_b_a)
          return colorPart === value;
        case 'rgb':
          // Match only RGB, ignoring alpha (r_g_b)
          const rgbPart = colorPart.split('_').slice(0, 3).join('_');
          return rgbPart === value;
        case 'contains':
          return colorPart.includes(value);
        default:
          return false;
      }
    });
  };

  // Load material textures (same as BackendModel)
  const loadMaterialTextures = async (materialKey) => {
    if (isMaterialCached(materialKey)) {
      console.log(`♻️ Using cached material for ${materialConfig[materialKey]?.name}`);
      return getCachedMaterial(materialKey);
    }

    const matConfig = materialConfig[materialKey];
    if (!matConfig) {
      console.error(`Material ${materialKey} not found in materialConfig`);
      return null;
    }

    console.log(`📦 Loading KTX2 textures for ${matConfig.name}...`);
    const textures = await loadKTX2MaterialTextures(matConfig, gl, true);

    Object.values(textures).forEach((texture) => {
      if (texture) {
        texture.repeat.set(matConfig.textureScale, matConfig.textureScale);
      }
    });

    const materialData = { textures, properties: matConfig.properties };
    setCachedMaterial(materialKey, materialData);
    return materialData;
  };

  const createMaterialFromConfig = (textures, properties) => {
    const materialOptions = {
      color: 0xffffff,
      roughness: properties.roughness,
      metalness: properties.metalness,
      flatShading: false,
    };

    if (textures.albedo) materialOptions.map = textures.albedo;
    if (textures.normal) {
      materialOptions.normalMap = textures.normal;
      materialOptions.normalScale = new THREE.Vector2(...properties.normalScale);
    }
    if (textures.ao) {
      materialOptions.aoMap = textures.ao;
      materialOptions.aoMapIntensity = properties.aoMapIntensity;
    }
    if (textures.height) {
      materialOptions.displacementMap = textures.height;
      materialOptions.displacementScale = properties.displacementScale;
    }
    if (textures.roughness) materialOptions.roughnessMap = textures.roughness;
    materialOptions.envMapIntensity = properties.envMapIntensity;

    // Use FrontSide - IFC normals should be correct
    materialOptions.side = THREE.FrontSide;

    return new THREE.MeshStandardMaterial(materialOptions);
  };

  // Convert IFC geometry to Three.js BufferGeometry
  // Returns array of { geometry, color } - one per unique color in the flatMesh
  // This ensures elements with multiple colors (like windows with frame+glass) create separate meshes
  const ifcGeometryToThree = (ifcApi, modelID, flatMesh, transparencyMap) => {
    // Group geometries by color (to keep glass separate from frame)
    const colorGroups = new Map(); // colorKey -> { geometries: [], color: {} }

    const makeColorKey = (c) => {
      if (!c) return 'default';
      return `${c.r.toFixed(3)}_${c.g.toFixed(3)}_${c.b.toFixed(3)}_${c.a.toFixed(3)}`;
    };

    for (let i = 0; i < flatMesh.geometries.size(); i++) {
      const placedGeometry = flatMesh.geometries.get(i);
      const geometryData = ifcApi.GetGeometry(modelID, placedGeometry.geometryExpressID);

      // Extract color from IFC geometry
      // web-ifc stores color as { x: R, y: G, z: B, w: Alpha } where values are 0-1
      const rawColor = placedGeometry.color;

      // Build color object
      let color = null;
      if (rawColor) {
        const r = typeof rawColor.x === 'number' ? rawColor.x : 0.8;
        const g = typeof rawColor.y === 'number' ? rawColor.y : 0.8;
        const b = typeof rawColor.z === 'number' ? rawColor.z : 0.8;
        let a = typeof rawColor.w === 'number' ? rawColor.w : 1.0;

        // Check if this color matches a transparent style (by RGB)
        const rgbKey = `${r.toFixed(3)}_${g.toFixed(3)}_${b.toFixed(3)}`;
        if (transparencyMap && transparencyMap.colorMap && transparencyMap.colorMap.has(rgbKey)) {
          a = transparencyMap.colorMap.get(rgbKey);
          console.log(`🪟 Found transparent color match: ${rgbKey} → opacity ${a.toFixed(2)}`);
        }

        color = { r, g, b, a };
      }

      const verts = ifcApi.GetVertexArray(geometryData.GetVertexData(), geometryData.GetVertexDataSize());
      const indices = ifcApi.GetIndexArray(geometryData.GetIndexData(), geometryData.GetIndexDataSize());

      if (verts.length === 0 || indices.length === 0) {
        geometryData.delete();
        continue;
      }

      const bufferGeometry = new THREE.BufferGeometry();

      // Vertices are interleaved: x, y, z, nx, ny, nz (6 floats per vertex)
      const positions = new Float32Array(verts.length / 2);
      const normals = new Float32Array(verts.length / 2);

      for (let j = 0; j < verts.length; j += 6) {
        const idx = j / 6;
        positions[idx * 3] = verts[j];
        positions[idx * 3 + 1] = verts[j + 1];
        positions[idx * 3 + 2] = verts[j + 2];
        normals[idx * 3] = verts[j + 3];
        normals[idx * 3 + 1] = verts[j + 4];
        normals[idx * 3 + 2] = verts[j + 5];
      }

      bufferGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      bufferGeometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
      bufferGeometry.setIndex(new THREE.BufferAttribute(indices, 1));

      // Apply transformation matrix
      const matrix = new THREE.Matrix4();
      matrix.fromArray(placedGeometry.flatTransformation);
      bufferGeometry.applyMatrix4(matrix);

      // Group by color
      const cKey = makeColorKey(color);
      if (!colorGroups.has(cKey)) {
        colorGroups.set(cKey, { geometries: [], color });
      }
      colorGroups.get(cKey).geometries.push(bufferGeometry);

      geometryData.delete();
    }

    // Return array of { geometry, color } - one per color group
    const results = [];
    for (const [cKey, { geometries, color }] of colorGroups) {
      if (geometries.length === 0) continue;

      const mergedGeometry = geometries.length === 1
        ? geometries[0]
        : mergeBufferGeometries(geometries);

      if (mergedGeometry) {
        results.push({ geometry: mergedGeometry, color });
      }
    }

    return results;
  };

  // Simple geometry merge
  const mergeBufferGeometries = (geometries) => {
    let totalVertices = 0;
    let totalIndices = 0;

    geometries.forEach(g => {
      totalVertices += g.attributes.position.count;
      totalIndices += g.index ? g.index.count : 0;
    });

    const mergedPositions = new Float32Array(totalVertices * 3);
    const mergedNormals = new Float32Array(totalVertices * 3);
    const mergedIndices = new Uint32Array(totalIndices);

    let vertexOffset = 0;
    let indexOffset = 0;
    let vertexCount = 0;

    geometries.forEach(g => {
      const positions = g.attributes.position.array;
      const normals = g.attributes.normal?.array || new Float32Array(positions.length);

      mergedPositions.set(positions, vertexOffset * 3);
      mergedNormals.set(normals, vertexOffset * 3);

      if (g.index) {
        const indices = g.index.array;
        for (let i = 0; i < indices.length; i++) {
          mergedIndices[indexOffset + i] = indices[i] + vertexOffset;
        }
        indexOffset += indices.length;
      }

      vertexOffset += g.attributes.position.count;
      g.dispose();
    });

    const merged = new THREE.BufferGeometry();
    merged.setAttribute('position', new THREE.BufferAttribute(mergedPositions, 3));
    merged.setAttribute('normal', new THREE.BufferAttribute(mergedNormals, 3));
    merged.setIndex(new THREE.BufferAttribute(mergedIndices, 1));

    return merged;
  };

  // Generate box projection UVs (same as BackendModel)
  // For vertical textures (like wood cladding), we want:
  // - V axis along the height (Y in world space)
  // - U axis along the horizontal direction of the wall
  const ensureGeometrySetup = (geometry, meshName = 'unknown', forceOrientation = null) => {
    const orientationKey = forceOrientation || 'auto';
    const lastOrientation = geometry.userData?.uvOrientation;

    if (lastOrientation === orientationKey && geometry.attributes.uv) {
      return;
    }

    if (!geometry.attributes.normal) {
      geometry.computeVertexNormals();
    }

    geometry.userData = geometry.userData || {};
    geometry.userData.uvOrientation = orientationKey;

    const positions = geometry.attributes.position;
    const normals = geometry.attributes.normal;
    const uvs = new Float32Array(positions.count * 2);

    geometry.computeBoundingBox();
    const bbox = geometry.boundingBox;

    // Determine the dominant normal direction for this geometry
    // by averaging all vertex normals
    let avgNx = 0, avgNy = 0, avgNz = 0;
    for (let i = 0; i < positions.count; i++) {
      avgNx += normals.getX(i);
      avgNy += normals.getY(i);
      avgNz += normals.getZ(i);
    }
    avgNx /= positions.count;
    avgNy /= positions.count;
    avgNz /= positions.count;

    // Log bounding box dimensions to help debug UV issues
    const bboxSizeX = bbox.max.x - bbox.min.x;
    const bboxSizeY = bbox.max.y - bbox.min.y;
    const bboxSizeZ = bbox.max.z - bbox.min.z;

    // Determine if this is a vertical wall or horizontal surface
    // Method 1: Use average normal direction
    let isVerticalWall = Math.abs(avgNy) < 0.5; // Normal is mostly horizontal = vertical wall
    let isHorizontalSurface = Math.abs(avgNy) > 0.7; // Normal is mostly vertical = floor/ceiling

    // Method 2: If normals cancel out (avgNormal ≈ 0), use bounding box aspect ratio
    // A floor/ceiling is typically much thinner in Y than in X and Z
    const normalMagnitude = Math.sqrt(avgNx*avgNx + avgNy*avgNy + avgNz*avgNz);
    if (normalMagnitude < 0.3) {
      // Normals cancelled out - use geometry shape to determine orientation
      const horizontalExtent = Math.max(bboxSizeX, bboxSizeZ);
      const verticalExtent = bboxSizeY;
      const aspectRatio = horizontalExtent / Math.max(verticalExtent, 1);

      // If much wider than tall (aspect ratio > 5), it's likely a floor/ceiling
      if (aspectRatio > 5) {
        isHorizontalSurface = true;
        isVerticalWall = false;
        console.log(`   🔄 Normals cancelled out, using bbox aspect ratio (${aspectRatio.toFixed(1)}) → HORIZONTAL SURFACE`);
      } else if (verticalExtent > horizontalExtent * 0.5) {
        isVerticalWall = true;
        isHorizontalSurface = false;
        console.log(`   🔄 Normals cancelled out, using bbox aspect ratio (${aspectRatio.toFixed(1)}) → VERTICAL WALL`);
      }
    }

    console.log(`🧭 ${meshName}: avgNormal=(${avgNx.toFixed(2)}, ${avgNy.toFixed(2)}, ${avgNz.toFixed(2)}) → ${isVerticalWall ? 'VERTICAL WALL' : isHorizontalSurface ? 'HORIZONTAL SURFACE' : 'ANGLED'}`);
    console.log(`   📏 BBox size: X=${bboxSizeX.toFixed(0)}mm, Y=${bboxSizeY.toFixed(0)}mm, Z=${bboxSizeZ.toFixed(0)}mm`);

    const uvScale = 1.0;

    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      const z = positions.getZ(i);

      const nx = normals ? Math.abs(normals.getX(i)) : 0;
      const ny = normals ? Math.abs(normals.getY(i)) : 0;
      const nz = normals ? Math.abs(normals.getZ(i)) : 0;

      let u, v;

      if (forceOrientation === 'vertical') {
        // Force vertical orientation: V = height (Y), U = horizontal extent
        if (nx > ny && nx > nz) {
          // Wall facing X direction
          u = (z - bbox.min.z) * uvScale;
          v = (y - bbox.min.y) * uvScale;
        } else if (nz > nx && nz > ny) {
          // Wall facing Z direction
          u = (x - bbox.min.x) * uvScale;
          v = (y - bbox.min.y) * uvScale;
        } else {
          // Floor/ceiling - use X for U
          u = (x - bbox.min.x) * uvScale;
          v = (z - bbox.min.z) * uvScale;
        }
      } else if (forceOrientation === 'horizontal') {
        // Force horizontal orientation
        if (nx > ny && nx > nz) {
          u = (y - bbox.min.y) * uvScale;
          v = (z - bbox.min.z) * uvScale;
        } else if (nz > nx && nz > ny) {
          u = (y - bbox.min.y) * uvScale;
          v = (x - bbox.min.x) * uvScale;
        } else {
          u = (x - bbox.min.x) * uvScale;
          v = (y - bbox.min.y) * uvScale;
        }
      } else {
        // Auto mode: Use the GEOMETRY's dominant orientation (not per-vertex)
        // This ensures consistent UVs across the entire mesh
        if (isHorizontalSurface) {
          // Floor or ceiling - ALWAYS use XZ plane for all vertices
          // This is the key fix: don't look at per-vertex normals for horizontal surfaces
          u = (x - bbox.min.x) * uvScale;
          v = (z - bbox.min.z) * uvScale;
        } else if (isVerticalWall) {
          // This is a vertical wall - V should be height (Y)
          // Use per-vertex normal to determine U direction (which horizontal axis)
          if (nx > nz) {
            // Wall facing mostly X direction - U along Z
            u = (z - bbox.min.z) * uvScale;
          } else {
            // Wall facing mostly Z direction - U along X
            u = (x - bbox.min.x) * uvScale;
          }
          v = (y - bbox.min.y) * uvScale;
        } else {
          // Angled surface - use per-vertex normals
          if (nx > ny && nx > nz) {
            u = (z - bbox.min.z) * uvScale;
            v = (y - bbox.min.y) * uvScale;
          } else if (ny > nx && ny > nz) {
            u = (x - bbox.min.x) * uvScale;
            v = (z - bbox.min.z) * uvScale;
          } else {
            u = (x - bbox.min.x) * uvScale;
            v = (y - bbox.min.y) * uvScale;
          }
        }
      }

      uvs[i * 2] = u;
      uvs[i * 2 + 1] = v;
    }

    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    if (!geometry.attributes.uv2) {
      geometry.setAttribute('uv2', geometry.attributes.uv);
    }
  };

  // List ALL IFC materials for debugging
  const listAllIfcMaterials = (ifcApi, modelID) => {
    console.log('📋 === ALL IFC MATERIALS ===');
    try {
      const materialIds = ifcApi.GetLineIDsWithType(modelID, WebIFC.IFCMATERIAL);
      console.log(`Found ${materialIds.size()} IfcMaterial entities:`);

      for (let i = 0; i < materialIds.size(); i++) {
        const matId = materialIds.get(i);
        const mat = ifcApi.GetLine(modelID, matId);
        console.log(`  #${matId}: "${mat.Name?.value || 'unnamed'}"`);
      }
    } catch (e) {
      console.warn('Could not list materials:', e);
    }
    console.log('📋 === END MATERIALS ===');

    // Also log IfcWindow and IfcDoor elements to help identify glass
    console.log('📋 === WINDOWS & DOORS ===');
    try {
      const windowIds = ifcApi.GetLineIDsWithType(modelID, WebIFC.IFCWINDOW);
      console.log(`Found ${windowIds.size()} IfcWindow entities`);
      for (let i = 0; i < windowIds.size(); i++) {
        const winId = windowIds.get(i);
        const win = ifcApi.GetLine(modelID, winId);
        console.log(`  Window #${winId}: "${win.Name?.value || 'unnamed'}"`);
      }

      const doorIds = ifcApi.GetLineIDsWithType(modelID, WebIFC.IFCDOOR);
      console.log(`Found ${doorIds.size()} IfcDoor entities`);
      for (let i = 0; i < doorIds.size(); i++) {
        const doorId = doorIds.get(i);
        const door = ifcApi.GetLine(modelID, doorId);
        console.log(`  Door #${doorId}: "${door.Name?.value || 'unnamed'}"`);
      }

      // Also check for IfcPlate which might be glass panels
      const plateIds = ifcApi.GetLineIDsWithType(modelID, WebIFC.IFCPLATE);
      console.log(`Found ${plateIds.size()} IfcPlate entities (could be glass panels)`);
    } catch (e) {
      console.warn('Could not list windows/doors:', e);
    }
    console.log('📋 === END WINDOWS & DOORS ===');
  };

  // Build transparency map - returns both color-based and expressID-based maps
  // The color-based map is used to match geometry colors during streaming
  const buildTransparencyMap = (ifcApi, modelID) => {
    const colorMap = new Map(); // "r_g_b" -> opacity (0-1)
    const expressIdMap = new Map(); // expressID -> opacity (0-1)

    try {
      // First, list all materials for debugging
      listAllIfcMaterials(ifcApi, modelID);

      // Step 1: Get all IfcSurfaceStyleRendering with transparency and their colors
      const renderingIds = ifcApi.GetLineIDsWithType(modelID, WebIFC.IFCSURFACESTYLERENDERING);
      console.log(`🔍 Scanning ${renderingIds.size()} IfcSurfaceStyleRendering for transparency...`);

      for (let i = 0; i < renderingIds.size(); i++) {
        const renderingId = renderingIds.get(i);
        try {
          const rendering = ifcApi.GetLine(modelID, renderingId);
          const t = rendering.Transparency?.value ?? rendering.Transparency ?? 0;
          if (t > 0) {
            const opacity = 1 - t;

            // Get the surface color - this is the key we'll match against geometry colors
            const surfaceColourRef = rendering.SurfaceColour;
            if (surfaceColourRef?.value) {
              const colorRgb = ifcApi.GetLine(modelID, surfaceColourRef.value);
              const r = colorRgb.Red?.value ?? colorRgb.Red ?? 0;
              const g = colorRgb.Green?.value ?? colorRgb.Green ?? 0;
              const b = colorRgb.Blue?.value ?? colorRgb.Blue ?? 0;

              // Create key with 3 decimal places (matching geometry color precision)
              const rgbKey = `${r.toFixed(3)}_${g.toFixed(3)}_${b.toFixed(3)}`;
              colorMap.set(rgbKey, opacity);
              console.log(`🪟 Transparent color #${renderingId}: rgb(${(r*255).toFixed(0)},${(g*255).toFixed(0)},${(b*255).toFixed(0)}) → ${(t*100).toFixed(0)}% transparent (opacity=${opacity.toFixed(2)}, key=${rgbKey})`);
            }
          }
        } catch (e) { /* ignore */ }
      }

      console.log(`🎨 Found ${colorMap.size} transparent colors: ${Array.from(colorMap.keys()).join(', ')}`);

      // Also mark window/plate types for reference
      const windowIds = ifcApi.GetLineIDsWithType(modelID, WebIFC.IFCWINDOW);
      const plateIds = ifcApi.GetLineIDsWithType(modelID, WebIFC.IFCPLATE);
      console.log(`📋 Found ${windowIds.size()} windows and ${plateIds.size()} plates in model`);

    } catch (e) {
      console.warn('⚠️ Could not build transparency map:', e);
    }

    return { colorMap, expressIdMap };
  };

  // Build material map from IFC - maps IFC material names to expressIds
  const buildMaterialMap = async (ifcApi, modelID) => {
    const materialMap = new Map();

    try {
      // Get all IfcRelAssociatesMaterial relationships
      const relIds = ifcApi.GetLineIDsWithType(modelID, WebIFC.IFCRELASSOCIATESMATERIAL);

      for (let i = 0; i < relIds.size(); i++) {
        const relId = relIds.get(i);
        const rel = ifcApi.GetLine(modelID, relId);

        if (!rel.RelatingMaterial || !rel.RelatedObjects) continue;

        // Get material name
        let materialName = null;
        const materialRef = rel.RelatingMaterial;

        if (materialRef.value) {
          const material = ifcApi.GetLine(modelID, materialRef.value);
          materialName = material.Name?.value || `Material_${materialRef.value}`;
        }

        if (!materialName) continue;

        // Get related objects (elements with this material)
        const relatedObjects = rel.RelatedObjects;
        if (!materialMap.has(materialName)) {
          materialMap.set(materialName, []);
        }

        for (let j = 0; j < relatedObjects.length; j++) {
          const objRef = relatedObjects[j];
          if (objRef.value) {
            materialMap.get(materialName).push(objRef.value);
          }
        }
      }

      console.log('📊 IFC Material Map:', Object.fromEntries(materialMap));
    } catch (e) {
      console.warn('⚠️ Could not build material map, using element-based fallback', e);
    }

    return materialMap;
  };

  // Load IFC model
  const loadIfcModel = async (ifcBuffer) => {
    const ifcApi = ifcApiRef.current;

    // Robust check: ensure ifcApi exists AND OpenModel method is available
    if (!ifcApi || typeof ifcApi.OpenModel !== 'function') {
      console.warn('⏳ IFC API not fully initialized yet, retrying in 500ms...');
      setTimeout(() => loadIfcModel(ifcBuffer), 500);
      return;
    }

    console.log('📥 Loading IFC model...');
    window.perfTracker?.logRenderStart();
    isLoadingNewModelRef.current = true;

    // Cleanup previous model
    const objectsToRemove = [];
    scene.traverse((child) => {
      if (child.userData?.fromBackend) {
        objectsToRemove.push(child);
      }
    });

    objectsToRemove.forEach((oldObj) => {
      scene.remove(oldObj);
      oldObj.traverse((child) => {
        if (child.isMesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
    });

    // Close previous model
    if (modelIdRef.current !== null) {
      try {
        ifcApi.CloseModel(modelIdRef.current);
      } catch (e) {
        console.warn('⚠️ Could not close previous model', e);
      }
    }

    // Open new model
    const uint8Array = new Uint8Array(ifcBuffer);
    const modelID = ifcApi.OpenModel(uint8Array);
    modelIdRef.current = modelID;

    console.log(`✅ IFC model opened, modelID: ${modelID}`);

    // Build material map
    const materialMap = await buildMaterialMap(ifcApi, modelID);
    materialMapRef.current = materialMap;

    // Build transparency map (by color from IfcSurfaceStyleRendering)
    const transparencyMap = buildTransparencyMap(ifcApi, modelID);

    // Create container for all meshes
    const container = new THREE.Group();
    container.userData.fromBackend = true;

    // Get all meshes from IFC and group by material
    meshMapRef.current.clear();
    const defaultMaterial = new THREE.MeshStandardMaterial({
      color: 0xcccccc,
      roughness: 0.7,
      metalness: 0.1,
      side: THREE.FrontSide
    });

    // Stream all meshes - group by BOTH material name AND color
    // This ensures elements with different colors don't get merged even if they share a material
    const meshGeometries = new Map(); // "materialName|colorKey" -> { geometries: [], color: null, expressIds: [], materialName: string }
    let meshCount = 0;

    // Helper to create a unique key for a color
    const colorKey = (color) => {
      if (!color) return 'none';
      return `${color.r.toFixed(3)}_${color.g.toFixed(3)}_${color.b.toFixed(3)}_${color.a.toFixed(3)}`;
    };

    console.log('🔄 Streaming IFC meshes...');
    // Log all window expressIDs for reference
    try {
      const windowIds = ifcApi.GetLineIDsWithType(modelID, WebIFC.IFCWINDOW);
      const windowExpressIds = [];
      for (let i = 0; i < windowIds.size(); i++) {
        windowExpressIds.push(windowIds.get(i));
      }
      console.log('🪟 Window expressIDs to look for:', windowExpressIds);
    } catch(e) { /* ignore */ }

    ifcApi.StreamAllMeshes(modelID, (flatMesh) => {
      meshCount++;
      const expressID = flatMesh.expressID;

      // Find which material this element belongs to
      let elementMaterialName = 'default';
      for (const [matName, ids] of materialMap.entries()) {
        if (ids.includes(expressID)) {
          elementMaterialName = matName;
          break;
        }
      }

      // Convert geometry and extract IFC colors from geometry stream
      // This now returns an ARRAY of { geometry, color } - one per unique color
      // This allows windows to have separate frame and glass meshes
      const geometryResults = ifcGeometryToThree(ifcApi, modelID, flatMesh, transparencyMap);
      if (geometryResults.length === 0) {
        console.log(`  ⚠️ No geometry for expressID ${expressID}`);
        return;
      }

      // Process each geometry/color pair separately
      for (const { geometry, color: geometryColor } of geometryResults) {
        if (!geometry) continue;

        // Determine the color to use (geometry stream color)
        const effectiveColor = geometryColor ? { ...geometryColor } : { r: 0.8, g: 0.8, b: 0.8, a: 1.0 };

        // Create a unique key combining material name and color
        // This ensures elements with the same material but different colors stay separate
        const groupKey = `${elementMaterialName}|${colorKey(effectiveColor)}`;

        if (!meshGeometries.has(groupKey)) {
          meshGeometries.set(groupKey, {
            geometries: [],
            color: effectiveColor,
            expressIds: [],
            materialName: elementMaterialName
          });
        }
        const entry = meshGeometries.get(groupKey);
        entry.geometries.push(geometry);
        entry.expressIds.push(expressID);
      }
    });

    console.log(`📊 Streamed ${meshCount} meshes, grouped into ${meshGeometries.size} materials`);

    // Create one mesh per material/color group
    console.log('🔧 Creating Three.js meshes...');
    for (const [groupKey, { geometries, color, expressIds, materialName }] of meshGeometries.entries()) {
      if (geometries.length === 0) continue;

      // Log color info with transparency
      const colorInfo = color
        ? `rgb(${(color.r*255).toFixed(0)},${(color.g*255).toFixed(0)},${(color.b*255).toFixed(0)}) alpha=${color.a.toFixed(2)}`
        : 'none (using default grey)';
      console.log(`  Processing ${materialName} (${groupKey}): ${geometries.length} geometries, color: ${colorInfo}`);

      const mergedGeometry = geometries.length === 1
        ? geometries[0]
        : mergeBufferGeometries(geometries);

      if (!mergedGeometry) {
        console.log(`  ⚠️ Failed to merge geometries for ${materialName}`);
        continue;
      }

      // Log geometry info
      const vertCount = mergedGeometry.attributes.position?.count || 0;
      console.log(`  📐 ${materialName}: ${vertCount} vertices`);

      // Create material with IFC color if available
      let material;
      if (color) {
        const isTransparent = color.a < 0.99;
        material = new THREE.MeshStandardMaterial({
          color: new THREE.Color(color.r, color.g, color.b),
          roughness: isTransparent ? 0.1 : 0.7, // Glass should be smooth
          metalness: isTransparent ? 0.0 : 0.1,
          transparent: isTransparent,
          opacity: color.a,
          side: THREE.FrontSide,
          depthWrite: !isTransparent // Disable depth write for transparent materials
        });
        if (isTransparent) {
          console.log(`  🪟 Transparent material for ${materialName}: opacity=${color.a.toFixed(2)}`);
        }
      } else {
        material = defaultMaterial.clone();
      }

      const mesh = new THREE.Mesh(mergedGeometry, material);
      mesh.name = groupKey; // Use groupKey for unique identification
      mesh.userData.ifcMaterialName = materialName; // Keep material name for selector matching
      mesh.userData.groupKey = groupKey;
      mesh.userData.originalColor = color; // Store original IFC color for reset
      mesh.userData.expressIds = expressIds; // Store express IDs for debugging
      mesh.castShadow = !color || color.a >= 0.99; // Don't cast shadow for transparent objects
      mesh.receiveShadow = true;
      mesh.visible = true;

      // Store by groupKey for unique access
      meshMapRef.current.set(groupKey, mesh);
      container.add(mesh);

      console.log(`  ✓ Created mesh: ${groupKey} (material: ${materialName})`);
    }

    // Scale and rotate
    // IFC is in mm, scale down to match scene units
    // Use 0.01 like OBJ does (1000mm = 10 units in scene)
    container.scale.set(0.01, 0.01, 0.01);
    // IFC uses Z-up coordinate system, Three.js uses Y-up
    // No rotation needed if model is already correctly oriented
    // container.rotateX(-Math.PI / 2);  // Removed - IFC already in correct orientation
    container.position.set(0, 0, 0);

    console.log(`📦 Container: scale=${container.scale.x}, children=${container.children.length}`);

    scene.add(container);
    currentModelRef.current = container;

    console.log(`📊 Created ${meshMapRef.current.size} material-based meshes`);

    // Notify parent about meshMap for debug panel
    if (onMeshMapReady) {
      onMeshMapReady(meshMapRef);
    }

    // Apply materials from form
    await applyMaterialsFromForm(container);

    // Setup camera (only on first load)
    if (!hasInitializedCamera.current) {
      setupCamera(container);
      hasInitializedCamera.current = true;
    } else {
      updateControlsTarget(container);
    }

    isLoadingNewModelRef.current = false;
    console.log('✅ IFC model loading complete');
    window.perfTracker?.logRenderEnd();
  };

  // Apply materials based on form config
  const applyMaterialsFromForm = async (container) => {
    if (!container || !formValues) return;

    console.log('🎨 Applying materials from form...');
    // Log unique material names (not groupKeys)
    const uniqueMaterialNames = new Set();
    meshMapRef.current.forEach((mesh) => {
      uniqueMaterialNames.add(mesh.userData.ifcMaterialName);
    });
    console.log('📊 Available IFC materials:', Array.from(uniqueMaterialNames));

    // DON'T hide all meshes - keep them visible by default
    // Only apply custom materials where selectors match

    // Helper to apply material to a mesh
    const applyMaterialToMesh = async (mesh, groupKey, selectedMaterialKey, configName) => {
      const materialData = await loadMaterialTextures(selectedMaterialKey);
      const matConfig = materialConfig[selectedMaterialKey];

      if (materialData && matConfig) {
        // Generate UVs for texture mapping (IFC geometry doesn't have UVs)
        // Pass null for orientation to use auto-detection, not the materialConfig orientation
        ensureGeometrySetup(mesh.geometry, groupKey, null);

        if (!materialData.material) {
          materialData.material = createMaterialFromConfig(materialData.textures, materialData.properties);
        }

        mesh.material = materialData.material;
        mesh.visible = true;
        mesh.castShadow = true;
        mesh.receiveShadow = true;

        // Apply unitsPerRepeat from materialConfig to scale UVs correctly
        // IFC geometry is in mm, so we divide by unitsPerRepeat to get the correct texture tiling
        const unitsPerRepeat = matConfig.unitsPerRepeat || 1000; // Default 1m
        const uvScaleFactor = 1 / unitsPerRepeat; // Convert mm to texture repeats

        // Scale the UVs on the geometry
        const geometry = mesh.geometry;
        if (geometry.attributes.uv) {
          const uvs = geometry.attributes.uv;
          const scaledUvs = new Float32Array(uvs.array.length);
          for (let i = 0; i < uvs.array.length; i++) {
            scaledUvs[i] = uvs.array[i] * uvScaleFactor;
          }
          geometry.setAttribute('uv', new THREE.BufferAttribute(scaledUvs, 2));
          geometry.attributes.uv.needsUpdate = true;

          // Also update uv2 for AO maps
          if (geometry.attributes.uv2) {
            geometry.setAttribute('uv2', new THREE.BufferAttribute(scaledUvs.slice(), 2));
          }
        }

        console.log(`  ✓ Applied ${selectedMaterialKey} to ${groupKey} (${configName}, unitsPerRepeat: ${unitsPerRepeat})`);

        // TEMPORARILY DISABLED: TextureManager UV manipulation for IFC debugging
        /*
        // Apply TextureManager if enabled
        if (USE_TEXTURE_MANAGER && mesh.material.map) {
          const checkAndApply = () => {
            if (mesh.material.map && mesh.material.map.image) {
              textureManagerRef.current.applyToMesh(mesh, {
                type: matConfig.type || 'non-directional',
                unitsPerRepeat: matConfig.unitsPerRepeat || 500,
                materialConfig: matConfig,
                forceDirection: null,
                orientation: matConfig.orientation || null,
                rotationOffset: 0,
                squareThreshold: 1.2
              });
            } else {
              setTimeout(checkAndApply, 50);
            }
          };
          setTimeout(checkAndApply, 100);
        }
        */
      }
    };

    // Find materialPicker configs and apply
    for (const config of formConfig) {
      // Support ifcColorSelector - match on IFC geometry color
      if (config.type === 'materialPicker' && config.ifcColorSelector) {
        const selectedMaterialKey = formValues[config.name];
        if (!selectedMaterialKey) continue;

        for (const [groupKey, mesh] of meshMapRef.current.entries()) {
          if (matchesColorSelector(groupKey, config.ifcColorSelector)) {
            await applyMaterialToMesh(mesh, groupKey, selectedMaterialKey, config.name);
          }
        }
      }

      // Support ifcMaterialSelector - match on IFC material name
      if (config.type === 'materialPicker' && config.ifcMaterialSelector) {
        const selectedMaterialKey = formValues[config.name];
        if (!selectedMaterialKey) continue;

        // Find matching IFC materials - use userData.ifcMaterialName for matching
        for (const [groupKey, mesh] of meshMapRef.current.entries()) {
          const ifcMatName = mesh.userData.ifcMaterialName;

          // Check if IFC material name matches selector
          if (config.ifcMaterialExcluder && isExcluded(ifcMatName, config.ifcMaterialExcluder)) {
            continue;
          }

          if (matchesMeshSelector(ifcMatName, config.ifcMaterialSelector)) {
            await applyMaterialToMesh(mesh, groupKey, selectedMaterialKey, config.name);
          }
        }
      }

      // Also support legacy meshSelector for backwards compatibility
      if (config.type === 'materialPicker' && config.meshSelector && !config.ifcMaterialSelector && !config.ifcColorSelector) {
        const selectedMaterialKey = formValues[config.name];
        if (!selectedMaterialKey) continue;

        for (const [groupKey, mesh] of meshMapRef.current.entries()) {
          const ifcMatName = mesh.userData.ifcMaterialName;
          if (config.meshExcluder && isExcluded(ifcMatName, config.meshExcluder)) {
            continue;
          }

          if (matchesMeshSelector(ifcMatName, config.meshSelector)) {
            await applyMaterialToMesh(mesh, groupKey, selectedMaterialKey, config.name);
          }
        }
      }
    }

    // Apply colors where colorPicker matches
    applyFormColors(container);
  };

  const applyFormColors = (container) => {
    if (!container || !formValues) return;

    console.log('🎨 Applying colors...');

    for (const config of formConfig) {
      if (config.type === 'colorPicker') {
        const selectedColorCode = formValues[config.name];
        const colorOption = config.options?.find(opt => opt.code === selectedColorCode);
        if (!colorOption) continue;

        for (const [groupKey, mesh] of meshMapRef.current.entries()) {
          const ifcMatName = mesh.userData.ifcMaterialName;
          let matches = false;

          // Check ifcColorSelector first (match on color)
          if (config.ifcColorSelector && matchesColorSelector(groupKey, config.ifcColorSelector)) {
            matches = true;
          }
          // Then check ifcMaterialSelector or meshSelector (match on material name)
          else {
            const selector = config.ifcMaterialSelector || config.meshSelector;
            if (selector) {
              const excluder = config.ifcMaterialExcluder || config.meshExcluder;
              if (excluder && isExcluded(ifcMatName, excluder)) continue;
              if (matchesMeshSelector(ifcMatName, selector)) {
                matches = true;
              }
            }
          }

          if (matches) {
            // Determine opacity priority:
            // 1. colorOption.opacity (per-color setting in options array)
            // 2. config.opacity (global setting for this picker)
            // 3. originalColor.a (preserve original IFC transparency)
            // 4. 1.0 (default opaque)
            const originalColor = mesh.userData?.originalColor;
            const optionOpacity = colorOption.opacity; // Per-color opacity in options
            const configOpacity = config.opacity; // Global opacity for this picker
            const opacity = optionOpacity !== undefined ? optionOpacity
                          : configOpacity !== undefined ? configOpacity
                          : (originalColor?.a ?? 1.0);
            const isTransparent = opacity < 0.99;

            // Create new material with color and transparency
            mesh.material = new THREE.MeshStandardMaterial({
              color: colorOption.color,
              roughness: isTransparent ? 0.1 : 0.7,
              metalness: isTransparent ? 0.0 : 0.1,
              transparent: isTransparent,
              opacity: opacity,
                side: THREE.FrontSide,
              depthWrite: !isTransparent
            });

            mesh.visible = true;
            mesh.castShadow = !isTransparent;
            mesh.receiveShadow = true;

            if (isTransparent) {
              console.log(`  🪟 Applied color with transparency: ${colorOption.color} opacity=${opacity.toFixed(2)}`);
            }
          }
        }
      }
    }
  };

  const setupCamera = (container) => {
    const box = new THREE.Box3().setFromObject(container);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);

    console.log(`📐 Model size: ${size.x.toFixed(2)} x ${size.y.toFixed(2)} x ${size.z.toFixed(2)}`);
    console.log(`📐 Model center: ${center.x.toFixed(2)}, ${center.y.toFixed(2)}, ${center.z.toFixed(2)}`);

    const fov = camera.fov * (Math.PI / 180);
    const aspect = camera.aspect;
    const fovVertical = fov;
    const fovHorizontal = 2 * Math.atan(Math.tan(fov / 2) * aspect);
    const effectiveFov = Math.min(fovVertical, fovHorizontal);
    const marginFactor = 1.5;
    const cameraDistance = (maxDim / 2) / Math.tan(effectiveFov / 2) * marginFactor;

    const cameraAngle = new THREE.Vector3(1.5, 0.5, 1.5).normalize();
    const cameraPosition = cameraAngle.multiplyScalar(cameraDistance).add(center);

    camera.position.copy(cameraPosition);
    camera.lookAt(center);

    // Fix clipping planes based on model size
    // Use very small near plane to allow close-up viewing
    camera.near = 0.01;
    camera.far = maxDim * 20;
    camera.updateProjectionMatrix();

    console.log(`📷 Camera near: ${camera.near}, far: ${camera.far}`);

    if (controls) {
      controls.target.copy(center);
      controls.minDistance = maxDim * 0.3;
      controls.maxDistance = maxDim * 5;
      controls.update();
    }
  };

  const updateControlsTarget = (container) => {
    const box = new THREE.Box3().setFromObject(container);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);

    // Update clipping planes
    camera.near = 0.01;
    camera.far = maxDim * 20;
    camera.updateProjectionMatrix();

    if (controls) {
      controls.target.copy(center);
      controls.minDistance = maxDim * 0.3;
      controls.maxDistance = maxDim * 5;
      controls.update();
    }
  };

  // Load IFC when data changes
  useEffect(() => {
    if (ifcData && isIfcReady) {
      console.log(`🔄 ifcData changed (ID: ${ifcData.requestId}), loading new model...`);
      loadIfcModel(ifcData.content);
    }
  }, [ifcData, isIfcReady]);

  // Update materials when form values change
  useEffect(() => {
    if (currentModelRef.current && formValues && !isLoadingNewModelRef.current) {
      console.log('🎨 Form values changed, updating materials...');
      applyMaterialsFromForm(currentModelRef.current);
    }
  }, [formValues]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentModelRef.current) {
        scene.remove(currentModelRef.current);
        currentModelRef.current.traverse((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        });
      }
      if (ifcApiRef.current && modelIdRef.current !== null) {
        try {
          ifcApiRef.current.CloseModel(modelIdRef.current);
        } catch (e) {}
      }
      textureManagerRef.current.clear();
    };
  }, []);

  return null;
};
